/**
 * Accent ramp derivation for owner branding.
 *
 * An owner picks ONE brand colour. The interface needs eight tonal steps of it —
 * a faint tint for chip backgrounds, a mid tone for borders, a dark tone for
 * text on light surfaces, a light tone for text on the dark sidebar.
 *
 * The naive approach (lighten/darken by a fixed percentage) produces ramps that
 * are washed out for dark inputs and muddy for light ones, and it loses the
 * tonal structure that makes the product look like itself. Instead this module
 * preserves the owner's HUE and SATURATION but pins LIGHTNESS to the exact
 * profile measured from BarberPro's champagne gold. Any brand colour therefore
 * lands in the same tonal positions the design was built around.
 *
 * Guardrails, because an owner can pick anything:
 *   • saturation is clamped, so neon does not scream and grey still reads as an accent
 *   • lightness targets are fixed, so a very dark or very light pick cannot flatten the ramp
 *   • the foreground paired with the accent is chosen by measured contrast, not assumed to be white
 *
 * Status colours (danger, success, warning) are NOT derived from branding. A
 * destructive action stays red whatever an owner's brand is.
 */

export type AccentStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700;

export const ACCENT_STEPS: readonly AccentStep[] = [50, 100, 200, 300, 400, 500, 600, 700];

/**
 * BarberPro's champagne gold, used verbatim as LaundryPro's default.
 *
 * Hardcoded rather than derived so the shipped default is pixel-exact rather
 * than a close reconstruction. Derivation applies only once an owner sets a
 * custom colour.
 */
export const DEFAULT_ACCENT_RAMP: Readonly<Record<AccentStep, string>> = {
  50: "#FBF5E9",
  100: "#F1E4C4",
  200: "#E6D2A2",
  300: "#D8BC7E",
  400: "#C6A461",
  500: "#A87B34",
  600: "#8A6329",
  700: "#6B4C1E",
};

/**
 * Lightness targets, measured from the ramp above. These define the tonal
 * skeleton every derived ramp inherits.
 */
const LIGHTNESS_TARGETS: Readonly<Record<AccentStep, number>> = {
  50: 94.9,
  100: 85.7,
  200: 76.9,
  300: 67.1,
  400: 57.8,
  500: 43.1,
  600: 35.1,
  700: 26.9,
};

/** Below this, a colour reads as grey and the accent disappears. */
const MIN_SATURATION = 18;
/** Above this, mid-tones vibrate against the warm neutrals and look cheap. */
const MAX_SATURATION = 72;

export interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

const HEX_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isValidHex(value: string): boolean {
  return HEX_PATTERN.test(value.trim());
}

export function hexToRgb(hex: string): Rgb {
  const match = HEX_PATTERN.exec(hex.trim());
  if (!match) throw new Error(`Not a hex colour: ${hex}`);

  const body = match[1] ?? "";
  const full = body.length === 3 ? body.split("").map((c) => c + c).join("") : body;

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0").toUpperCase();
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l: l * 100 };

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let h: number;
  if (max === rn) h = ((gn - bn) / delta) % 6;
  else if (max === gn) h = (bn - rn) / delta + 2;
  else h = (rn - gn) / delta + 4;

  h *= 60;
  if (h < 0) h += 360;

  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sn = s / 100;
  const ln = l / 100;

  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  const m = ln - c / 2;
  return {
    r: (rgb[0] + m) * 255,
    g: (rgb[1] + m) * 255,
    b: (rgb[2] + m) * 255,
  };
}

export function hexToHsl(hex: string): Hsl {
  return rgbToHsl(hexToRgb(hex));
}

export function hslToHex(hsl: Hsl): string {
  return rgbToHex(hslToRgb(hsl));
}

/** WCAG relative luminance. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export const WHITE = "#FFFFFF";
/** ink-900 — the product's foreground, used when white would be unreadable. */
export const INK_900 = "#1C1917";

/**
 * Picks the readable foreground for a background, by measurement.
 *
 * A bright yellow brand colour would leave white text at roughly 1.5:1 —
 * illegible. Choosing by contrast rather than assuming white is what keeps an
 * owner from making their own buttons unreadable.
 */
export function readableForeground(backgroundHex: string): string {
  return contrastRatio(backgroundHex, WHITE) >= contrastRatio(backgroundHex, INK_900)
    ? WHITE
    : INK_900;
}

/** The sidebar surface. Steps rendered as text on it must stay legible. */
export const GRAPHITE_900 = "#141210";

/** WCAG AA for normal-size text. */
const MIN_TEXT_CONTRAST = 4.5;

/**
 * Walks lightness until the colour reaches a contrast target against a surface.
 *
 * Fixed lightness targets alone are not enough. A yellow or cyan brand colour is
 * intrinsically luminous: at the same lightness that makes a gold or teal
 * readable, yellow still measures about 3.4:1 on white. Without this, an owner
 * choosing yellow would silently get unreadable links and field labels.
 *
 * Steps in one-unit increments and gives up at the end of the range rather than
 * looping forever, returning the best it reached.
 */
function enforceContrast(
  hsl: Hsl,
  against: string,
  target: number,
  direction: "darken" | "lighten",
): string {
  let { l } = hsl;
  let candidate = hslToHex({ ...hsl, l });

  for (let i = 0; i < 100; i++) {
    if (contrastRatio(candidate, against) >= target) return candidate;
    l = direction === "darken" ? l - 1 : l + 1;
    if (l < 0 || l > 100) break;
    candidate = hslToHex({ ...hsl, l });
  }

  return candidate;
}

/**
 * Builds the eight-step accent ramp for an owner's brand colour.
 *
 * Returns BarberPro's exact gold when the input is that gold, so the default
 * installation is byte-identical to the measured reference — no derivation, no
 * contrast adjustment, no drift.
 */
export function buildAccentRamp(baseHex: string): Record<AccentStep, string> {
  if (!isValidHex(baseHex)) {
    return { ...DEFAULT_ACCENT_RAMP };
  }

  const normalised = rgbToHex(hexToRgb(baseHex));
  if (normalised.toUpperCase() === DEFAULT_ACCENT_RAMP[500]) {
    return { ...DEFAULT_ACCENT_RAMP };
  }

  const { h, s } = hexToHsl(normalised);
  const saturation = Math.max(MIN_SATURATION, Math.min(MAX_SATURATION, s));

  const ramp = {} as Record<AccentStep, string>;
  for (const step of ACCENT_STEPS) {
    ramp[step] = hslToHex({ h, s: saturation, l: LIGHTNESS_TARGETS[step] });
  }

  // Steps with a known text role get their contrast enforced. The rest keep the
  // measured tonal position, because they are only ever backgrounds or borders.
  //
  // 600 — links, focused field labels, accent text on white
  // 700 — the darkest accent text
  // 300 — the active navigation label on the graphite sidebar
  for (const step of [600, 700] as const) {
    ramp[step] = enforceContrast(
      { h, s: saturation, l: LIGHTNESS_TARGETS[step] },
      WHITE,
      MIN_TEXT_CONTRAST,
      "darken",
    );
  }

  ramp[300] = enforceContrast(
    { h, s: saturation, l: LIGHTNESS_TARGETS[300] },
    GRAPHITE_900,
    MIN_TEXT_CONTRAST,
    "lighten",
  );

  return ramp;
}

/** `"168 123 52"` — the channel-triple form CSS custom properties need. */
export function toRgbChannels(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
}
